/**
 * Questionnaire schemas — template library CRUD.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 *
 * Runtime fill validation for completed forms is **not** defined here — it is derived
 * dynamically from each `Questionnaire.item[].required` via
 * `lib/questionnaireResponseFillValidation.ts` (chart documents, assessments, portal forms).
 */
import { z } from 'zod';
import type {
  QuestionnaireStatus as FhirQStatus,
  TemplateCategoryCode as FhirTemplateCategory,
} from '@unify/fhir-model/resources/questionnaire';

/** FHIR Questionnaire.status values — synced with fhir-model. */
export const questionnaireStatusValues = [
  'active',
  'draft',
  'retired',
] as const satisfies readonly FhirQStatus[];

/** Unify template category codes — synced with fhir-model. */
export const templateCategoryValues = [
  'appointments',
  'measures',
  'treatment-planning',
  'forms',
  'administrative',
] as const satisfies readonly FhirTemplateCategory[];

/**
 * Template creation / duplication.
 */
export const createTemplateSchema = z.object({
  title: z.string().trim().min(1, 'Template title is required'),
  status: z.enum(questionnaireStatusValues).default('draft'),
  category: z.enum(templateCategoryValues).optional(),
  description: z.string().optional(),
});

export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;
